import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";
import BackToHomepageLink from "@/components/shared/BackToHomepageLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (err) {
      // The backend will only ever return 200 for the canonical
      // generic message — but if the network drops or rate-limit
      // (429) fires, surface that without leaking anything about
      // the email's existence.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429 && detail && typeof detail === "object") {
        setError(
          detail.message
          || "Too many password reset requests. Please wait an hour."
        );
      } else {
        setError("Something went wrong. Please try again in a moment.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#0B1120] p-6"
      data-testid="forgot-password-page"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-300"
            data-testid="forgot-back-to-login"
          >
            <ArrowLeft className="w-3 h-3" /> Back to sign in
          </Link>
          <BackToHomepageLink
            testId="forgot-back-to-homepage"
            label="Home"
          />
        </div>

        <Link
          to="/"
          className="flex items-center gap-3 mb-8 hover:opacity-80 transition-opacity w-fit"
          data-testid="forgot-logo-link"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
            <Shield className="w-6 h-6 text-slate-900" />
          </div>
          <div>
            <h1 className="font-barlow font-bold text-xl text-slate-50">
              Sentient Vault
            </h1>
            <span className="text-[9px] text-amber-400/70 tracking-[0.25em] uppercase font-manrope">
              Obligation-Enforced Finance
            </span>
          </div>
        </Link>

        {!submitted ? (
          <>
            <h2 className="font-barlow font-semibold text-2xl text-slate-50 mb-1">
              Reset your password
            </h2>
            <p className="text-sm text-slate-500 mb-8 leading-relaxed">
              Enter your email address and we’ll send password reset
              instructions if an account exists.
            </p>

            <form onSubmit={onSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className="text-xs text-slate-400 uppercase tracking-wider"
                >
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-slate-900/50 border-slate-700 text-slate-100 h-11"
                  data-testid="forgot-email-input"
                />
              </div>

              {error && (
                <div
                  className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg p-3"
                  data-testid="forgot-error"
                >
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full h-11 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900 font-semibold rounded-lg transition-all active:scale-[0.98]"
                data-testid="forgot-submit-btn"
              >
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          </>
        ) : (
          <div
            className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-6"
            data-testid="forgot-success"
          >
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-3" />
            <h2 className="font-barlow font-semibold text-xl text-slate-50 mb-2">
              Check your inbox
            </h2>
            <p className="text-sm text-slate-400 leading-relaxed">
              If an account exists for this email, a password reset
              link has been sent. The link expires in 30 minutes and
              can only be used once.
            </p>
            <Link
              to="/login"
              className="inline-block mt-6 text-sm text-amber-400 hover:text-amber-300"
              data-testid="forgot-success-back-link"
            >
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
